import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { SelectField } from "components/AddVaccineForm/components";
import { useVaccineOutbreaks } from "features/immunizations/hooks/useVaccineOutbreaks";
import {
  isOutbreakProgramme,
  outbreaksCoveringDate,
  toOutbreakOptions,
  VACCINATION_PROGRAMME,
  VACCINATION_PROGRAMME_OPTIONS,
} from "features/immunizations/utils/vaccinationProgramme";

/**
 * Which programme an MMR is being given under (KIN-1875, NIIS Phase 2 §3).
 *
 * A child needing an MMR before twelve months had it recorded in the PCI dose-1 slot,
 * because that was the only slot on offer, and the real PCI dose was then refused when it
 * came due. Choosing Travel or Outbreak here is what stops an out-of-schedule dose from
 * consuming the scheduled one.
 *
 * MMR only. Anywhere else the backend refuses a programme outright, so the control does
 * not appear rather than leading the clinician into a HARD_STOP.
 */
export const VaccinationProgrammeFields = ({ dateGiven }) => {
  const { watch, setValue } = useFormContext();
  const programme = watch("vaccinationProgramme");

  const { data: outbreaks = [] } = useVaccineOutbreaks();

  // Judged on the date the dose was given, never on today's: a dose given during an
  // outbreak stays recordable after the campaign closes, and the backend applies the same
  // window when it saves.
  const availableOutbreaks = useMemo(
    () => outbreaksCoveringDate(outbreaks, dateGiven),
    [outbreaks, dateGiven]
  );

  // No outbreak running is the normal state of the world, not a misconfiguration. Offering
  // Outbreak with nothing behind it would be a choice that cannot be completed, so the
  // option is simply absent until a campaign covers the date.
  const canRecordOutbreak = availableOutbreaks.length > 0;

  const programmeOptions = VACCINATION_PROGRAMME_OPTIONS.filter(
    (option) =>
      option.value !== VACCINATION_PROGRAMME.outbreak || canRecordOutbreak
  );

  // A date moved out of every campaign's window leaves a chosen outbreak stranded, and the
  // backend refuses a stray code on a dose that is no longer an Outbreak one.
  useEffect(() => {
    if (!isOutbreakProgramme(programme)) {
      if (watch("outbreakCode")) setValue("outbreakCode", "");

      return;
    }

    if (!canRecordOutbreak) {
      setValue("vaccinationProgramme", VACCINATION_PROGRAMME.primaryChildhood);
      setValue("outbreakCode", "");

      return;
    }

    const chosen = watch("outbreakCode");

    if (chosen && !availableOutbreaks.some(({ code }) => code === chosen)) {
      setValue("outbreakCode", "");
    }
  }, [programme, canRecordOutbreak, availableOutbreaks, setValue, watch]);

  return (
    <>
      <SelectField
        name="vaccinationProgramme"
        label="Vaccination Programme"
        options={programmeOptions}
        placeholder="Primary Childhood Immunisation"
      />

      {!!isOutbreakProgramme(programme) && (
        <SelectField
          name="outbreakCode"
          label="Outbreak"
          options={toOutbreakOptions(availableOutbreaks)}
          placeholder="Select"
          required
          ariaRequired
        />
      )}
    </>
  );
};
